import { Select, TextInput } from "@bcc-ui/ui-components";
import * as React from "react";
import { useTranslation } from "react-i18next";

interface Props {
    existingPaths: string[];
    selectedPath: string;
    newFolder: string;
    pathLabel: string;
    newFolderLabel: string;
    newFolderPlaceholder: string;
    pathSelectId: string;
    newFolderInputId: string;
    newFolderInputTestId?: string;
    onSelectedPathChange: (path: string) => void;
    onNewFolderChange: (folder: string) => void;
}

const FOLDER_INDENT = "\u2003";

const formatFolderLabel = (path: string): string => {
    const segments = path.split("/");
    const depth = segments.length - 1;
    if (depth === 0) {
        return path;
    }
    return `${FOLDER_INDENT.repeat(depth)}└ ${segments[segments.length - 1]}`;
};

const FolderPathSelector = ({
    existingPaths,
    selectedPath,
    newFolder,
    pathLabel,
    newFolderLabel,
    newFolderPlaceholder,
    pathSelectId,
    newFolderInputId,
    newFolderInputTestId,
    onSelectedPathChange,
    onNewFolderChange,
}: Props): JSX.Element => {
    const { t } = useTranslation();

    if (existingPaths.length === 0) {
        // No existing folders yet — a single input serves as the full path entry field.
        return (
            <TextInput
                name={newFolderInputId}
                label={newFolderLabel}
                value={newFolder}
                placeholder={newFolderPlaceholder}
                onChange={(e) => onNewFolderChange(e.target.value)}
                testIds={newFolderInputTestId ? { input: newFolderInputTestId } : undefined}
            />
        );
    }

    const pathItems = [
        { value: "", label: t("FileSharing.rootPath") },
        ...existingPaths.map((path) => ({ value: path, label: formatFolderLabel(path) })),
    ];

    return (
        <>
            <Select
                name={pathSelectId}
                label={pathLabel}
                placeholder={pathLabel}
                selectedByDefault={selectedPath}
                items={pathItems}
                onSelection={(value) => onSelectedPathChange(String(value))}
                closeOnSelection={true}
                showTooltips={true}
            />
            <TextInput
                name={newFolderInputId}
                label={newFolderLabel}
                value={newFolder}
                placeholder={newFolderPlaceholder}
                onChange={(e) => onNewFolderChange(e.target.value)}
                testIds={newFolderInputTestId ? { input: newFolderInputTestId } : undefined}
            />
        </>
    );
};

export default FolderPathSelector;
